import React from 'react';
import type { Creator } from '@/types';
import { getPlatformConfig } from '../../../utils/platformConfig';
import parseHandle from '../../../utils/handles';
import Modal from './Modal';
import { useAdminRole } from '../../../hooks/core';

interface CreatorModalProps {
  creator: Creator | null;
  open: boolean;
  onClose: () => void;
  role?: string;
}

const CreatorModal: React.FC<CreatorModalProps> = ({ creator, open, onClose }) => {
  const { isAdmin } = useAdminRole();
  // Only show for viewers
  if (isAdmin || !open || !creator) return null;

  const resolvedHandle = (() => {
    let handle = creator.handle;
    if (!handle && creator.profileUrls && creator.profileUrls.length > 0) {
      const parsed = parseHandle(creator.profileUrls[0]);
      handle = parsed.handle;
    }
    return handle ? handle : 'Unknown Creator';
  })();

  return (
    <Modal isOpen={open} onClose={onClose} title={resolvedHandle}>
      <div className="flex flex-col items-center space-y-4 mt-3">
        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-blue-200 mb-2">
          {creator.image ? (
            <img src={creator.image} alt={creator.handle || 'Creator'} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center theme-muted theme-text-muted text-5xl">?</div>
          )}
        </div>
        <div className="text-center">
          {creator.profileUrls && creator.profileUrls.length > 0 && (
            <div className="flex flex-wrap justify-center items-center gap-2 mt-2">
              {creator.profileUrls.slice(0, 4).map((url, idx) => {
                const Icon = getPlatformConfig(url).icon;
                const color = getPlatformConfig(url).color;
                return (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center hover:underline text-lg"
                    title={url}
                  >
                    <Icon size={24} color={color} />
                  </a>
                );
              })}
              {creator.profileUrls.length > 4 && (
                <div className="relative group">
                  <span className="inline-flex items-center justify-center bg-gray-200 rounded-full px-2 py-1 text-xs cursor-pointer ml-1">+{creator.profileUrls.length - 4} more</span>
                  <div className="absolute z-10 left-1/2 -translate-x-1/2 mt-2 w-48 theme-surface border theme-border rounded shadow-lg p-2 hidden group-hover:block">
                    {creator.profileUrls.slice(4).map((url, idx) => {
                      const Icon = getPlatformConfig(url).icon;
                      const color = getPlatformConfig(url).color;
                      return (
                        <a
                          key={idx}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 text-sm mb-1 hover:underline"
                          title={url}
                        >
                          <Icon size={24} color={color} />
                          <span className="truncate">{url}</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="w-full mt-4 space-y-2">
          <div>
            <span className="font-semibold theme-text">Rates:</span>{' '}
            <span className="theme-text">{creator.rates ? `$${creator.rates.toLocaleString()}` : 'Not specified'}</span>
          </div>
          <div>
            <span className="font-semibold theme-text">Deliverables:</span>{' '}
            <span className="theme-text">{creator.deliverables || 'Not specified'}</span>
          </div>
          <div>
            <span className="font-semibold theme-text">Reason:</span>{' '}
            <span className="theme-text">{creator.reason || 'Not specified'}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreatorModal; 